import $ from "jquery";

class AddToWishlistButton {
  constructor(buttonSelector, options = {}) {
    this.button = $(buttonSelector);
    if (!this.button.length) return;

    this.options = {
      apiEndpoint: "/wishlist/addajax",
      indexEndpoint: "/wishlist/mywishlistajax",
      ...options,
    };

    // If preloaded data is provided, use it instead of making API call
    if (options.preloadedWishlistItem) {
      this.initWithPreloadedData(options.preloadedWishlistItem);
    } else {
      this.initState();
    }

    this.bindEvents();
  }

  initWithPreloadedData(wishlistItem) {
    // Use preloaded data instead of making API call
    // Now both logged-in and guest users use numeric ID
    if (wishlistItem && wishlistItem.id > 0) {
      this.setWishlistId(wishlistItem.id);
      this.setActive(true);
    } else {
      this.setActive(false);
    }
  }

  async initState() {
    // Kiểm tra xem WishlistManager đã khởi tạo chưa
    if (window.WishlistManager && window.WishlistManager.isInitialized) {
      return;
    }

    // Kiểm tra trạng thái wishlist khi khởi tạo (fallback for single button usage)
    const productId = this.getProductId();
    if (!productId) return;

    try {
      const response = await this.fetchWishlistState(productId);
      if (
        response.success &&
        Array.isArray(response.items) &&
        response.items.length > 0
      ) {
        const item = response.items[0];
        // Now both logged-in and guest users use numeric ID
        if (item.id > 0) {
          this.setWishlistId(item.id);
          this.setActive(true);
        }
      } else {
        this.setActive(false);
      }
    } catch (error) {
      this.setActive(false);
    }
  }

  setActive(isActive) {
    // Đổi icon SVG theo trạng thái
    const svg = this.button.find("svg");

    if (isActive) {
      // Fill trái tim đỏ
      if (svg.length > 0) {
        svg.find("path").attr({
          fill: "#ef4444", // đỏ
          stroke: "#ef4444",
        });
        svg.removeClass("wishlist-loading");
      }
      this.button.addClass("text-red-500");
    } else {
      // Outline đen
      if (svg.length > 0) {
        svg.find("path").attr({
          fill: "none",
          stroke: "#111827", // đen
        });
        svg.removeClass("wishlist-loading");
      }
      this.button.removeClass("text-red-500");
    }
  }

  bindEvents() {
    this.button.on("click", async (e) => {
      e.preventDefault();
      const wishlistId = this.getWishlistId();
      // Now both logged-in and guest users use numeric ID
      if (wishlistId > 0) {
        // Đã yêu thích, gọi API xoá
        try {
          this.showLoading();
          const response = await this.removeFromWishlistAPI(wishlistId);
          if (response.success) {
            this.setWishlistId(0);
            this.setActive(false);
            this.showRemoved();
            this.button.trigger("wishlist:removed", {
              wishlist_id: wishlistId,
              response: response,
            });
          } else {
            throw new Error(
              response.error?.join(". ") || "Failed to remove from wishlist"
            );
          }
        } catch (error) {
          this.showError(error.message);
        }
        return;
      }
      this.handleAddToWishlist();
    });
  }

  getProductId() {
    return this.button.data("product-id");
  }

  getFrom() {
    return this.button.data("from");
  }

  getWishlistId() {
    return this.button.data("id");
  }

  setWishlistId(id) {
    this.button.data("id", id);
    this.button.attr("data-id", id);
  }

  showLoading() {
    // Hiện spinner loader thay cho icon trái tim
    const svg = this.button.find("svg");
    svg.hide();
    if (this.button.find(".wishlist-spinner").length === 0) {
      this.button.append(`
        <span class="wishlist-spinner animate-spin ml-0.5" style="display:inline-block;width:20px;height:20px;vertical-align:middle;">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="#ef4444" stroke-width="4"></circle>
            <path class="opacity-75" fill="#ef4444" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
          </svg>
        </span>
      `);
    }
    this.button.prop("disabled", true);
  }

  hideLoading() {
    // Ẩn spinner, hiện lại icon trái tim
    this.button.find(".wishlist-spinner").remove();
    this.button.find("svg").show();
    this.button.prop("disabled", false);
  }

  showSuccess() {
    this.hideLoading();
    this.setActive(true);

    // Notify WishlistManager about the change
    if (window.WishlistManager) {
      const productId = this.getProductId();
      const wishlistId = this.getWishlistId();
      if (productId && wishlistId) {
        window.WishlistManager.updateWishlistData(productId, {
          id: wishlistId,
          product_id: productId,
        });
      }
    }
  }

  showError(message) {
    this.hideLoading();
    // Có thể show toast hoặc alert nếu muốn
  }

  async handleAddToWishlist() {
    const productId = this.getProductId();
    const from = this.getFrom();
    if (!productId || !from) {
      return;
    }
    try {
      this.showLoading();
      const response = await this.addToWishlistAPI(productId, from);
      if (response.success && response.wishlist && response.wishlist.id) {
        this.setWishlistId(response.wishlist.id);
        this.showSuccess();
        // Có thể emit event nếu cần
        this.button.trigger("wishlist:added", {
          product_id: productId,
          from: from,
          wishlist_id: response.wishlist.id,
          response: response,
        });
      } else {
        throw new Error(
          response.error?.join(". ") || "Failed to add to wishlist"
        );
      }
    } catch (error) {
      this.showError(error.message);
    }
  }

  async addToWishlistAPI(productId, from) {
    const response = await fetch(this.options.apiEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({
        product_id: parseInt(productId),
        from: parseInt(from),
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async fetchWishlistState(productId) {
    const url =
      this.options.indexEndpoint +
      "?product_id=" +
      encodeURIComponent(productId);
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  showRemoved() {
    this.hideLoading();
    this.setActive(false);

    // Notify WishlistManager about the change
    if (window.WishlistManager) {
      const productId = this.getProductId();
      if (productId) {
        window.WishlistManager.updateWishlistData(productId, null);
      }
    }
  }

  async removeFromWishlistAPI(wishlistId) {
    const url = `/wishlist/removeajax?id=${encodeURIComponent(wishlistId)}`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }
}

export default AddToWishlistButton;
